import { useCallback, useEffect, useState } from "react";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, CalendarDays, MapPin, Users } from "lucide-react";
import { SEED_EVENT, isSeedEventVisible } from "@/data/seedEvent";

const empty = {
  name: "",
  event_date: "",
  location: "",
  description: "",
  expected_guests: "",
  event_type: "vendor",
  status: "upcoming",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Events() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/events");
      setItems(data || []);
    } catch (err) {
      console.error("[Events] load failed:", err?.message || err);
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.event_date) return toast.error("Date is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        event_date: new Date(form.event_date).toISOString(),
        expected_guests: form.expected_guests ? parseInt(form.expected_guests, 10) : null,
      };
      await api.post("/events", payload);
      toast.success("Event added");
      setForm(empty);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add event");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Event removed");
    } catch (_e) {
      toast.error("Failed to remove");
    }
  };

  const now = new Date();
  const userUpcoming = items.filter((e) => new Date(e.event_date) >= now);
  const past = items.filter((e) => new Date(e.event_date) < now);
  const showSeed = isSeedEventVisible();
  const upcoming = showSeed ? [SEED_EVENT, ...userUpcoming] : userUpcoming;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="events-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Events</h1>
        <p className="text-slate-500 mt-1 text-sm">Festivals, pop-ups, private gigs — keep them all in one place.</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="events-form">
        <h2 className="text-base font-semibold">Add an event</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="e-name">Name</Label>
            <Input id="e-name" value={form.name} onChange={(e) => update("name", e.target.value)} required data-testid="event-name" />
          </div>
          <div>
            <Label htmlFor="e-date">When</Label>
            <Input
              id="e-date"
              type="datetime-local"
              value={form.event_date}
              onChange={(e) => update("event_date", e.target.value)}
              required
              data-testid="event-date"
            />
          </div>
          <div>
            <Label htmlFor="e-loc">Location</Label>
            <Input id="e-loc" value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="Riverside Park" data-testid="event-location" />
          </div>
          <div>
            <Label htmlFor="e-guests">Expected guests</Label>
            <Input
              id="e-guests"
              type="number"
              min="0"
              value={form.expected_guests}
              onChange={(e) => update("expected_guests", e.target.value)}
              data-testid="event-guests"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="e-desc">Description</Label>
          <Textarea
            id="e-desc"
            rows={3}
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            data-testid="event-description"
          />
        </div>
        <div className="flex justify-end">
          <Button type="submit" className="ff-btn-primary" disabled={saving} data-testid="event-add">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus size={14} className="mr-1" />} Add event
          </Button>
        </div>
      </form>

      <section className="space-y-3">
        <h2 className="text-base font-semibold">Upcoming · {upcoming.length}</h2>
        {loading ? (
          <div className="text-slate-500 text-sm">Loading…</div>
        ) : upcoming.length === 0 ? (
          <div className="text-slate-500 text-sm bg-white border border-dashed border-slate-200 rounded-xl p-8 text-center" data-testid="events-empty">
            <CalendarDays className="mx-auto mb-2 text-slate-400" />
            No upcoming events yet.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="events-upcoming-list">
            {upcoming.map((ev) => (
              <li key={ev.id} className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`event-${ev.id}`}>
                <div className="flex-shrink-0 bg-[#FDECE2] text-[#E8650A] rounded-lg p-3">
                  <CalendarDays size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate flex items-center gap-2">
                    {ev.name}
                    {ev.is_platform_event && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide bg-[#FDECE2] text-[#E8650A] px-2 py-0.5 rounded-full" data-testid={`event-platform-badge-${ev.id}`}>
                        Platform event
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {ev.is_platform_event
                      ? `${ev.date_label} · ${ev.time_range_label}`
                      : formatDate(ev.event_date)}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-500">
                    {ev.location && <span className="inline-flex items-center gap-1"><MapPin size={12} />{ev.location}</span>}
                    {ev.expected_guests ? <span className="inline-flex items-center gap-1"><Users size={12} />{ev.expected_guests}</span> : null}
                  </div>
                  {ev.description && <p className="text-sm text-slate-600 mt-1">{ev.description}</p>}
                </div>
                {!ev.is_platform_event && (
                  <Button variant="ghost" size="sm" onClick={() => remove(ev.id)} data-testid={`event-delete-${ev.id}`}>
                    <Trash2 size={14} className="mr-1" /> Remove
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {past.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-base font-semibold text-slate-500">Past · {past.length}</h2>
          <ul className="space-y-2 opacity-75" data-testid="events-past-list">
            {past.map((ev) => (
              <li key={ev.id} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3 text-sm">
                <CalendarDays size={16} className="text-slate-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{ev.name}</div>
                  <div className="text-xs text-slate-500">{formatDate(ev.event_date)} {ev.location && `· ${ev.location}`}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => remove(ev.id)} data-testid={`event-delete-${ev.id}`}>
                  <Trash2 size={14} />
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
